import {getFileExtension, type DiffDelta} from '@github-ui/diff-file-tree/diff-file-tree-helpers'

import {useMemo} from 'react'
// eslint-disable-next-line no-restricted-imports
import type {
  Codeowners,
  CodeownersPathOwnership,
  PullRequestFileTreeDiff,
} from '@github-ui/pull-requests/page-data/payloads/file-tree'
import {forceAnnouncementToScreenReaders} from '../utils/force-announcement-to-screen-reader'
import type {FileFilterState} from '../components/FileFilter'
import {getOwnershipByPath, useCodeownersData} from '../page-data/loaders/use-codeowners-data'

export function useFileFiltering(
  diffSummaries: PullRequestFileTreeDiff[],
  filters: FileFilterState,
  basePath: string,
): [Map<string, boolean>, DiffDelta[]] {
  const {data: codeownersData} = useCodeownersData({basePath})

  const [memoizedHiddenFilepathMap, memoizedFilteredDiffSummaries] = useMemo(() => {
    const hiddenFilepathMap = new Map<string, boolean>()
    const filteredDiffSummaries: DiffDelta[] = []

    // Iterate over diff summaries to build:
    // 1. Map of file paths to their "hidden by filter" state
    // 2. Array of filtered diff summaries ("filtered" means not hidden in this case)
    for (const diffSummary of diffSummaries ?? []) {
      const isOwnedByViewer = (
        getOwnershipByPath({
          diffPath: diffSummary.path,
          codeownersData: codeownersData as Codeowners | undefined,
        }) as CodeownersPathOwnership
      ).isOwnedByViewer

      if (matchesFilter(diffSummary, filters, isOwnedByViewer)) {
        filteredDiffSummaries.push(diffSummary)
        hiddenFilepathMap.set(diffSummary.path, false)
      } else {
        hiddenFilepathMap.set(diffSummary.path, true)
      }
    }
    return [hiddenFilepathMap, filteredDiffSummaries]
  }, [diffSummaries, codeownersData, filters])

  // Announce filter results to screen readers
  const filteredCount = memoizedFilteredDiffSummaries.length
  const announcementText = `${filteredCount} file${filteredCount === 1 ? '' : 's'} remain${
    filteredCount === 1 ? 's' : ''
  }`
  forceAnnouncementToScreenReaders(announcementText, 1000)

  return [memoizedHiddenFilepathMap, memoizedFilteredDiffSummaries]
}

function matchesFilter(diff: PullRequestFileTreeDiff, filters: FileFilterState, isOwnedByViewer: boolean): boolean {
  const match =
    diff.path.toLowerCase().includes(filters.filterText.toLowerCase()) &&
    !filters.unselectedFileExtensions.has(getFileExtension(diff.path)) &&
    (filters.showDeletedFiles || !(diff.changeType === 'REMOVED' || diff.changeType === 'DELETED')) &&
    (!filters.showOnlyManifestFiles || !!diff.isManifestFile) &&
    (!filters.showOnlyOwnedFiles || isOwnedByViewer) &&
    (filters.showViewedFiles || !diff.markedAsViewed) &&
    (filters.showVendoredFiles || !diff.isVendored)

  return match
}
